import "./TicketingCreateEdit.scss";
import { useLocation } from "react-router-dom";
import { useState, useEffect, useRef, useLayoutEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useAlert } from "react-alert";
import { useFirestoreErrorTranslation } from "../../../../utility/useFirestoreErrorTranslation";
import useEventValidationSchema from "../../../../components/validationsSchemas/useEventValidationSchema";
import useTicketsValidationSchema from "../../../../components/validationsSchemas/useTicketsValidationSchema";
import { getEventSubCollection } from "../../../../utility/getEventSubCollection";
import createUpdateEvent from "../../../../utility/createUpdateEvent";
import TicketForm from "../../../../components/ticketing/ticketForm/TicketForm";
import PictureDisplaySelector from "../../../../components/ticketing/pictureDisplaySelector/PictureDisplaySelector";
import FormField from "../../../../components/formField/FormField";
import TicketingMap from "../../../../components/ticketing/map/Map";
import ModalComponent from "../../../../components/modal/Modal";
import GreenRedOultinedButtons from "../../../../components/buttons/greenRedOutlinedButtons/GreenRedOutlinedButtons";
import GradientButton from "../../../../components/buttons/gradientButton/GradientButton";
import ConfirmDeleteModal from "../../../../components/deleteModal/DeleteModal";
import { Loader2 } from "../../../../helper/loader";
import backButton from "../../../../images/icons/back.png";
import { doc, deleteDoc } from "firebase/firestore";
import { db } from "../../../../firebase";
import ColorPickerModal from "../../../../components/colorPickerModal/ColorPickerModal";
import OutlinedPinkButton from "../../../../components/buttons/outlinedPinkButton/OutlinedPinkButton";
import i18n from "i18next";
import FormFieldsSettingsModal from "./settingsModal/FormFieldsSettingsModal";
import VisualSettingsModal from "./settingsModal/VisualSettingsModal";
import EmailCustomizationModal, {
  DEFAULT_TICKET_EMAIL_CUSTOMIZATION,
} from "./settingsModal/EmailCustomizationModal";
import duplicateTicketingEvent from "../../../../utility/duplicateTicketingEvent";
import {
  finalizeTicketingCover,
  generateTicketingCover,
  getTicketingCoverStatus,
  rewriteTicketingDescription,
  translateTicketingDescriptions,
} from "../../../../utility/aiApi";

const DEFAULT_MANDATORY_FIELDS = ["Full Name", "Email Address"];
const DEFAULT_OPTIONNAL_FIELDS = ["Phone number", "Age", "Address"];
const DEFAULT_PRIMARY_COLOR = "#4200ff";
const DEFAULT_SECONDARY_COLOR = "#ff02c7";

const mergeCoverCandidates = (current = [], incoming = []) => {
  const byPath = new Map();
  [...current, ...incoming].forEach((candidate) => {
    if (candidate?.storagePath) byPath.set(candidate.storagePath, candidate);
  });
  return Array.from(byPath.values()).slice(-3);
};

function TicketingSection({ id, eyebrow, title, description, children, className = "" }) {
  return (
    <section id={id} className={`ticketingEditorSection ${className}`.trim()}>
      <div className="ticketingEditorSectionHeader">
        <div>
          {eyebrow && <p className="ticketingEditorEyebrow">{eyebrow}</p>}
          <h2>{title}</h2>
          {description && <span>{description}</span>}
        </div>
      </div>
      <div className="ticketingEditorSectionBody">{children}</div>
    </section>
  );
}

function TicketingCreateEdit({ org }) {
  const location = useLocation();
  const user = useSelector((state) => state.userReducer);
  const [tickets, setTickets] = useState([]);
  const [showFormFieldsModal, setShowFormFieldsModal] = useState(false);
  const [showVisualSettingsModal, setShowVisualSettingsModal] = useState(false);
  const [showEmailCustomizationModal, setShowEmailCustomizationModal] = useState(false);
  const [ticketsHasMore, setTicketsHasMore] = useState(true);
  const [newCustomFieldError, setNewCustomFieldError] = useState(null);
  const navigate = useNavigate();
  const isEditing = !!location?.state?.event;
  const alert = useAlert();
  const [event, setEvent] = useState({
    name: "",
    endDate: "",
    eventFromDate: "",
    eventToDate: "",
    mediaUrl: "",
    formFields: {
      mandatoryFields: DEFAULT_MANDATORY_FIELDS.map((field) => ({
        value: field,
        checked: true,
      })),
      optionnalFields: DEFAULT_OPTIONNAL_FIELDS.map((field) => ({
        value: field,
        checked: false,
      })),
      customFields: [],
    },
    open: true,
    createdBy: user.users.uid,
    orgId: String(location.state?.orgId) || "",
    ansQues: [],
    subdivision: true,
    logoUrl: null,
    darkMode: true,
    mainColor1: DEFAULT_PRIMARY_COLOR,
    mainColor2: DEFAULT_SECONDARY_COLOR,
    maxEntries: null,
    mediaIsVideo: false,
    formRequiredOnSite: false,
    feeDelegated: false,
    ticketEmailCustomization: DEFAULT_TICKET_EMAIL_CUSTOMIZATION,
  });
  const [eventErrors, setEventErrors] = useState({});
  const [modalInformations, setModalInformations] = useState({
    content: "",
    title: "",
  });
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation("ticketingCreateEdit");
  const errorTranslation = useFirestoreErrorTranslation();
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [isDelete, setIsDelete] = useState(false);
  const [isHeaderActionsOpen, setIsHeaderActionsOpen] = useState(false);
  const eventValidationSchema = useEventValidationSchema();
  const ticketsValidationSchema = useTicketsValidationSchema();
  const [activeColor, setActiveColor] = useState(null);
  const logoFileInputRef = useRef();
  const [descriptions, setDescriptions] = useState({ [i18n.language]: "" });
  const [activeDescriptionLanguage, setActiveDescriptionLanguage] = useState(i18n.language);
  const aiDraftStorageKey = `ticketing-ai-draft-${location.state?.event?.id || location.state?.orgId || "new"}`;
  const [aiDraftId] = useState(() => {
    const existing = window.sessionStorage.getItem(aiDraftStorageKey);
    const value =
      existing ||
      window.crypto?.randomUUID?.() ||
      `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    window.sessionStorage.setItem(aiDraftStorageKey, value);
    return value;
  });
  const [generatedCovers, setGeneratedCovers] = useState([]);
  const [selectedGeneratedCover, setSelectedGeneratedCover] = useState(null);
  const [coverRemaining, setCoverRemaining] = useState(3);
  const [coverGenerating, setCoverGenerating] = useState(false);
  const [aiTextAction, setAiTextAction] = useState(null);
  const [aiTextPreview, setAiTextPreview] = useState(null);
  const [isAddingNewDescription, setIsAddingNewDescription] = useState(false);
  const allLanguages = i18n?.options?.supportedLngs?.filter((language) => language !== "cimode");
  const allSelected = allLanguages?.every((lang) => Object.keys(descriptions).includes(lang));
  const descriptionLanguages = useMemo(() => Object.keys(descriptions), [descriptions]);
  const activeDescription =
    descriptions[activeDescriptionLanguage] ?? descriptions[descriptionLanguages[0]] ?? "";
  const resolvedOrgId = String(
    org?.id || location.state?.event?.orgId || location.state?.orgId || "",
  );

  useEffect(() => {
    if (!resolvedOrgId) return;
    getTicketingCoverStatus({
      orgId: resolvedOrgId,
      draftId: aiDraftId,
      eventId: location.state?.event?.id || "",
    })
      .then((result) => {
        setGeneratedCovers((current) => mergeCoverCandidates(current, result.candidates || []));
        setCoverRemaining(result.remaining ?? 3);
      })
      .catch(() => {});
  }, [resolvedOrgId, aiDraftId, location.state?.event?.id]);

  useEffect(() => {
    if (!descriptionLanguages.length) return;
    if (!descriptionLanguages.includes(activeDescriptionLanguage)) {
      setActiveDescriptionLanguage(descriptionLanguages[0]);
    }
  }, [activeDescriptionLanguage, descriptionLanguages]);

  useEffect(() => {
    if (!location.state?.orgId && !location.state?.event) {
      navigate(-1);
    }
  }, [location.state?.orgId]);

  useLayoutEffect(() => {
    if (isEditing) {
      setLoading(true);
      const event = location?.state?.event;
      event.endDate = event.endDate ? new Date(event.endDate) : "";
      event.eventFromDate = event.eventFromDate ? new Date(event.eventFromDate) : "";
      event.eventToDate = event.eventToDate ? new Date(event.eventToDate) : "";
      const existingFields = event.formFields;
      event.formFields = {};
      event.formFields.mandatoryFields = DEFAULT_MANDATORY_FIELDS.map((field) => ({
        value: field,
        checked: true,
      }));
      event.formFields.optionnalFields = DEFAULT_OPTIONNAL_FIELDS.map((field) => ({
        value: field,
        checked: existingFields.includes(field),
      }));
      event.formFields.customFields = existingFields
        .map((field) => {
          if (
            !DEFAULT_OPTIONNAL_FIELDS.includes(field) &&
            !DEFAULT_MANDATORY_FIELDS.includes(field)
          ) {
            return { value: field.value ?? field, isMandatory: field.isMandatory ?? true };
          }
        })
        .filter(Boolean);
      event.mainColor1 = event?.mainColor1 ?? DEFAULT_PRIMARY_COLOR;
      event.mainColor2 = event?.mainColor2 ?? DEFAULT_SECONDARY_COLOR;
      event.logoUrl = event?.logoUrl ?? null;
      event.darkMode = event?.darkMode ?? true;
      event.maxEntries = event?.maxEntries ?? null;
      event.mediaUrl = event?.mediaUrl || event?.imageUrl;
      event.mediaIsVideo = event?.mediaIsVideo || false;
      event.formRequiredOnSite = event?.formRequiredOnSite || false;
      event.feeDelegated = event?.feeDelegated ?? false;
      event.ticketEmailCustomization = {
        ...DEFAULT_TICKET_EMAIL_CUSTOMIZATION,
        ...(event?.ticketEmailCustomization || {}),
      };
      delete event.ticketEmailMessage;

      if (event.descriptions) {
        setDescriptions({ ...event.descriptions });
        setActiveDescriptionLanguage(Object.keys(event.descriptions)[0] || i18n.language);
      } else {
        setDescriptions((prev) => ({ [i18n.language]: event.description }));
        setActiveDescriptionLanguage(i18n.language);
      }

      fetchTickets();
      setEvent({
        ...event,
        subdivision: event.subdivision ?? true,
      });
      setLoading(false);
    }
  }, [location?.state?.event]);

  async function fetchTickets() {
    if (ticketsHasMore && location?.state?.event) {
      try {
        const ticketsData = await getEventSubCollection({
          collectionName: "Events",
          docId: location?.state?.event.id,
          subCollectionName: "Tickets",
        });

        if (ticketsData.data.length > 0) {
          const sortedTickets = ticketsData.data
            .map((ticket, index) => ({
              ...ticket,
              order: ticket.order ?? index,
            }))
            .sort((a, b) => a.order - b.order);
          setTickets(sortedTickets);
        }
        setTicketsHasMore(false);
      } catch (error) {
        setModalInformations({
          title: t("errorRetrievesTickets"),
          content: errorTranslation(error),
        });
        console.error(t("errorRetrievesTickets"), error);
      }
    }
  }

  const extractOptionnalCheckedValues = (formFields) =>
    formFields.filter((f) => f.checked).map((f) => f.value);

  function customFieldVerification(valueName) {
    setNewCustomFieldError(null);
    let customFieldError;

    if (
      event.formFields.customFields.find(
        (field) => field.value.toLowerCase() === valueName.toLowerCase(),
      ) ||
      DEFAULT_MANDATORY_FIELDS.find((field) => field.toLowerCase() === valueName.toLowerCase()) ||
      DEFAULT_OPTIONNAL_FIELDS.find((field) => field.toLowerCase() === valueName.toLowerCase())
    ) {
      customFieldError = t("fieldAlreadyExists");
    } else if (valueName === "") {
      customFieldError = t("fieldCannotBeEmpty");
    }

    if (!customFieldError) {
      handleNewField(valueName, "customFields");
    } else {
      setNewCustomFieldError(customFieldError);
      alert.show(t("invalidFields"));
    }
  }

  async function validationForm() {
    setEventErrors({});
    setLoading(true);

    const eventFormatted = {
      ...event,
      formFields: [
        ...extractOptionnalCheckedValues(event.formFields.optionnalFields),
        ...event.formFields.customFields,
      ],
      mainColor1: event?.mainColor1 === DEFAULT_PRIMARY_COLOR ? null : event.mainColor1,
      mainColor2: event?.mainColor2 === DEFAULT_SECONDARY_COLOR ? null : event.mainColor2,
      descriptions: { ...descriptions },
      maxEntries: parseFloat(event.maxEntries) || null,
    };

    const [eventResult, ticketResult] = await Promise.allSettled([
      eventValidationSchema.validate(eventFormatted, { abortEarly: false }),
      ticketsValidationSchema.validate(tickets, { abortEarly: false }),
    ]);

    if (eventResult.status === "fulfilled" && ticketResult.status === "fulfilled") {
      updateEvent(eventFormatted);
    } else {
      const allErrors = [];

      if (eventResult.status === "rejected") {
        allErrors.push(...eventResult.reason.inner);
      }

      if (ticketResult.status === "rejected") {
        const ticketErrors = ticketResult.reason.inner || [];
        allErrors.push(
          ...ticketErrors.map((err) => ({
            ...err,
            path: err.path || "tickets",
          })),
        );
      }

      const errors = allErrors.reduce((acc, currentError) => {
        acc[currentError.path] = currentError.message;
        return acc;
      }, {});

      setEventErrors(errors);
      setModalInformations({
        title: t("errorDuringValidation"),
        content: t("formFieldsError"),
      });

      setLoading(false);
    }
  }

  function handleCreateUpdateTicket({
    id,
    name,
    price,
    numAvailable,
    maxUnitPerSale,
    limitPerEmail = false,
    description = null,
    isNew = false,
    secret = false,
    amountSold = 0,
    availableFrom = null,
    availableTo = null,
    image = null,
    options = [],
  }) {
    setTickets((prevTickets) => {
      const updatedTicket = {
        id: id,
        amountSold: amountSold,
        maxUnitPerSale,
        limitPerEmail: limitPerEmail === true,
        name,
        numAvailable,
        price,
        isNew: isNew,
        description,
        secret: !!secret,
        availableFrom,
        availableTo,
        image,
        options,
      };

      const index = prevTickets.findIndex((ticket) => ticket.id === updatedTicket.id);

      if (index !== -1) {
        const updatedTickets = [...prevTickets];
        updatedTickets[index] = { ...updatedTickets[index], ...updatedTicket };
        return updatedTickets;
      } else {
        updatedTicket.order = prevTickets.length;
        return [...prevTickets, updatedTicket];
      }
    });
  }

  function handleNewCoordinates({ latitude, longitude, latitudeDelta, longitudeDelta, address }) {
    setEvent((prevEvent) => ({
      ...prevEvent,
      eventAddress: address,
      eventLocation: {
        latitude,
        longitude,
        latitudeDelta,
        longitudeDelta,
      },
    }));
  }

  async function updateEvent(eventFormatted) {
    eventFormatted.endDate = new Date(event.endDate).getTime();
    eventFormatted.eventFromDate = new Date(event.eventFromDate).getTime();
    eventFormatted.eventToDate = new Date(event.eventToDate).getTime();
    eventFormatted.id = isEditing ? event.id : String(Date.now());
    delete eventFormatted.ticketEmailMessage;

    if (!isEditing) {
      eventFormatted.createdAt = eventFormatted.id;
    }

    try {
      if (selectedGeneratedCover) {
        const finalized = await finalizeTicketingCover({
          orgId: resolvedOrgId,
          eventId: eventFormatted.id,
          originalEventId: isEditing ? event.id : null,
          draftId: aiDraftId,
          storagePath: selectedGeneratedCover.storagePath,
        });
        eventFormatted.mediaUrl = finalized.mediaUrl;
        eventFormatted.mediaIsVideo = false;
        eventFormatted.mediaAlreadyUploaded = true;
      }
      await createUpdateEvent({
        eventFormatted: eventFormatted,
        tickets: tickets,
        isUpdate: isEditing,
        oldMediaUrl: location?.state?.event?.mediaUrl || location?.state?.event?.imageUrl,
        oldLogoUrl: location?.state?.event?.logoUrl,
        oldEmailHeaderImageUrl: location?.state?.event?.ticketEmailCustomization?.headerImageUrl,
      });

      if (!isEditing) window.sessionStorage.removeItem(aiDraftStorageKey);

      navigate("/dashboard/ticketing", { state: { selectedOrgId: org?.id } });
    } catch (error) {
      setModalInformations({
        title: t("errorCreatingEvent"),
        content: errorTranslation(error),
      });
      console.error(t("errorCreatingEvent"), error);
    }
    setLoading(false);
  }

  function updateState(name, value) {
    setEvent((prevEvent) => ({ ...prevEvent, [name]: value }));
  }

  function handleMediaChange(event) {
    const file = event.target.files[0];

    if (file) {
      if (file.size > 15000000) {
        alert.show(t("mediaTooLarge"));
      } else if (file.type.startsWith("image/") || file.type.startsWith("video/")) {
        setSelectedGeneratedCover(null);
        setEvent((prev) => ({ ...prev, mediaIsVideo: file.type.startsWith("video/") }));
        const url = URL.createObjectURL(file);
        setEvent((prevEvent) => ({ ...prevEvent, mediaUrl: url }));
      } else {
        alert.show(t("fileNotImageVideo"));
      }
    }
  }

  const getCoverDescription = () =>
    descriptions[i18n.language]?.trim() ||
    Object.values(descriptions)
      .find((description) => description?.trim())
      ?.trim() ||
    "";

  const handleGenerateCover = async () => {
    const description = getCoverDescription();
    if (!event.name.trim() || !description || coverRemaining <= 0) return;
    setCoverGenerating(true);
    try {
      const result = await generateTicketingCover({
        orgId: resolvedOrgId,
        eventId: isEditing ? event.id : null,
        draftId: aiDraftId,
        name: event.name.trim(),
        description,
      });
      setGeneratedCovers((current) =>
        mergeCoverCandidates(current, result.candidates || [result.candidate]),
      );
      setCoverRemaining(result.remaining);
      setSelectedGeneratedCover(result.candidate);
      setEvent((current) => ({
        ...current,
        mediaUrl: result.candidate.downloadUrl,
        mediaIsVideo: false,
      }));
    } catch (error) {
      alert.show(error.message, { type: "error" });
      if (error.code === "AI_COVER_LIMIT_REACHED") setCoverRemaining(0);
    } finally {
      setCoverGenerating(false);
    }
  };

  const handleSelectGeneratedCover = (candidate) => {
    setSelectedGeneratedCover(candidate);
    setEvent((current) => ({ ...current, mediaUrl: candidate.downloadUrl, mediaIsVideo: false }));
  };

  const handleRewriteDescription = async (language) => {
    const description = descriptions[language]?.trim();
    if (!event.name.trim() || !description) return;
    setAiTextAction({ type: "rewrite", language });
    try {
      const result = await rewriteTicketingDescription({
        orgId: resolvedOrgId,
        name: event.name.trim(),
        language,
        description,
      });
      setAiTextPreview({
        type: "rewrite",
        language,
        original: description,
        value: result.description,
      });
    } catch (error) {
      alert.show(error.message, { type: "error" });
    } finally {
      setAiTextAction(null);
    }
  };

  const handleTranslateDescriptions = async (sourceLanguage) => {
    const description = descriptions[sourceLanguage]?.trim();
    if (!event.name.trim() || !description) return;
    setAiTextAction({ type: "translations", language: sourceLanguage });
    try {
      const result = await translateTicketingDescriptions({
        orgId: resolvedOrgId,
        name: event.name.trim(),
        sourceLanguage,
        description,
      });
      setAiTextPreview({ type: "translations", sourceLanguage, translations: result.translations });
    } catch (error) {
      alert.show(error.message, { type: "error" });
    } finally {
      setAiTextAction(null);
    }
  };

  const applyAiTextPreview = () => {
    if (aiTextPreview?.type === "rewrite") {
      setDescriptions((current) => ({ ...current, [aiTextPreview.language]: aiTextPreview.value }));
    } else if (aiTextPreview?.type === "translations") {
      setDescriptions((current) => ({ ...current, ...aiTextPreview.translations }));
    }
    setAiTextPreview(null);
  };

  function handleFieldChange(fieldName, fieldValue, type) {
    const formFieldsCopy = { ...event.formFields };
    const indexToChange = formFieldsCopy[type].findIndex((field) => field.value === fieldName);

    if (indexToChange !== -1) {
      const updatedField = {
        ...formFieldsCopy[type][indexToChange],
        checked: fieldValue,
      };

      formFieldsCopy[type] = [
        ...formFieldsCopy[type].slice(0, indexToChange),
        updatedField,
        ...formFieldsCopy[type].slice(indexToChange + 1),
      ];
      updateState("formFields", formFieldsCopy);
    }
  }

  function handleNewField(fieldName, type) {
    const newFormFields = { ...event.formFields };
    newFormFields[type] = [...newFormFields[type], { value: fieldName, isMandatory: true }];
    updateState("formFields", newFormFields);
  }

  function handleDeleteField(fieldName, type) {
    const newFormFields = { ...event.formFields };
    newFormFields[type] = newFormFields[type].filter((field) => field.value !== fieldName);
    updateState("formFields", newFormFields);
  }

  function handleDeleteTicket(index) {
    setTickets((prev) => prev.filter((_, i) => i !== index));
  }

  function handleReorderTickets(oldIndex, newIndex) {
    setTickets((prevTickets) => {
      const result = Array.from(prevTickets);
      const [removed] = result.splice(oldIndex, 1);
      result.splice(newIndex, 0, removed);
      return result.map((ticket, index) => ({
        ...ticket,
        order: index,
      }));
    });
  }

  function handleUpdateCustomFields(fieldName, isMandatory) {
    setEvent((prevEvent) => {
      const index = prevEvent.formFields.customFields.findIndex(
        (customField) => customField.value === fieldName,
      );

      if (index === -1) return prevEvent;

      return {
        ...prevEvent,
        formFields: {
          ...prevEvent.formFields,
          customFields: prevEvent.formFields.customFields.map((field, i) =>
            i === index ? { ...field, isMandatory } : field,
          ),
        },
      };
    });
  }

  async function handleDuplicateEvent() {
    try {
      setLoading(true);
      setIsDuplicate(false);
      await duplicateTicketingEvent({
        eventId: location?.state?.event?.id,
        orgId: resolvedOrgId,
      });
      navigate("/dashboard/ticketing", { state: { selectedOrgId: org?.id } });
    } catch (error) {
      setModalInformations({
        title: t("errorWhenDuplicatingEvent"),
        content: errorTranslation(error),
      });
      console.error(t("errorWhenDuplicatingEvent"), error);
    } finally {
      setLoading(false);
    }
  }

  function handleDeleteEvent() {
    try {
      const eventRef = doc(db, "Events", event.id);
      deleteDoc(eventRef);
      navigate("/dashboard/ticketing", { state: { selectedOrgId: org?.id } });
    } catch (error) {
      setModalInformations({
        title: t("errorWhenDuplicatingEvent"),
        content: errorTranslation(error),
      });
      console.error(t("errorWhenDeletingEvent"), error);
    }
  }

  function ValidationButtons() {
    return (
      <div className="ticketingValidationActions">
        <div className="ticketingPrimaryValidation">
          <GradientButton onPress={validationForm}>
            {isEditing ? t("updateSale") : t("createSale")}
          </GradientButton>
        </div>
      </div>
    );
  }

  const handleSetColor = (newColor) => {
    if (activeColor === "color1") {
      updateState("mainColor1", newColor);
    } else if (activeColor === "color2") {
      updateState("mainColor2", newColor);
    }
  };

  function handleLogoChange(e) {
    const file = e.target.files[0];

    if (file && file.type.startsWith("image/")) {
      const objectUrl = URL.createObjectURL(file);
      updateState("logoUrl", objectUrl);
    }
  }

  function LanguageButtons() {
    return (
      <div className="modalLanguageButtons">
        {allLanguages?.map((language) => {
          const available = Object?.keys(descriptions)?.includes(language);
          if (!available) {
            return (
              <button
                key={language}
                onClick={() => {
                  setDescriptions((prev) => ({ ...prev, [language]: "" }));
                  setActiveDescriptionLanguage(language);
                  setIsAddingNewDescription(false);
                }}
              >
                {language}
              </button>
            );
          }
        })}
      </div>
    );
  }

  const pageSections = [
    { id: "ticketing-essential", label: t("ticketingSectionEssential") },
    { id: "ticketing-content", label: t("ticketingSectionContent") },
    { id: "ticketing-customization", label: t("ticketingSectionCustomization") },
    { id: "ticketing-settings", label: t("ticketingSectionSettings") },
    { id: "ticketing-tickets", label: t("ticketingSectionTickets") },
  ];

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="ticketingCreateEditMainContainer">
      {loading && <Loader2 />}
      <ModalComponent
        isOpen={!!modalInformations.title}
        onClose={() => setModalInformations({ title: "", content: "" })}
        title={modalInformations.title}
        message={modalInformations.content}
      />

      <ModalComponent
        isOpen={isAddingNewDescription}
        onClose={() => setIsAddingNewDescription(false)}
        title={t("chooseLanguageNewDescription")}
        actionsComponent={<LanguageButtons />}
      />

      <ModalComponent
        isOpen={!!aiTextPreview}
        onClose={() => setAiTextPreview(null)}
        onValidate={applyAiTextPreview}
        validateLabel={
          aiTextPreview?.type === "rewrite" ? t("acceptAiProposal") : t("applyAiTranslations")
        }
        title={
          aiTextPreview?.type === "rewrite" ? t("aiRewritePreview") : t("aiTranslationPreview")
        }
        modalClassName="aiTextPreviewModal"
        overlayClassName="aiTextPreviewOverlay"
        actionsComponent={
          aiTextPreview?.type === "rewrite" ? (
            <div className="aiTextPreview">
              <label>{t("aiOriginalDescription")}</label>
              <textarea value={aiTextPreview.original} readOnly />
              <label>{t("aiProposal")}</label>
              <textarea
                value={aiTextPreview.value}
                onChange={(event) =>
                  setAiTextPreview((current) => ({ ...current, value: event.target.value }))
                }
              />
            </div>
          ) : aiTextPreview?.type === "translations" ? (
            <div className="aiTextPreview">
              {Object.entries(aiTextPreview.translations || {}).map(([language, value]) => (
                <div key={language}>
                  <label>{language.toUpperCase()}</label>
                  <textarea
                    value={value}
                    onChange={(event) =>
                      setAiTextPreview((current) => ({
                        ...current,
                        translations: { ...current.translations, [language]: event.target.value },
                      }))
                    }
                  />
                </div>
              ))}
            </div>
          ) : null
        }
      />

      <ColorPickerModal
        isOpen={activeColor}
        onClose={() => setActiveColor(null)}
        color={activeColor === "color1" ? event?.mainColor1 : event?.mainColor2}
        setColor={handleSetColor}
      />

      <ConfirmDeleteModal
        isOpen={isDuplicate || isDelete}
        onClose={() => {
          setIsDelete(false);
          setIsDuplicate(false);
        }}
        onConfirm={() => {
          if (isDelete) {
            handleDeleteEvent();
          } else {
            handleDuplicateEvent();
          }
        }}
        message={isDuplicate ? t("confirmDuplicate") : t("confirmDelete")}
      />

      <div className="creationForm">
        <div className="ticketingEditorTopBar">
          <div className="titleContainer">
            <button
              className="buttonBackContainer"
              onClick={() => navigate("/dashboard/ticketing", { state: { selectedOrgId: org?.id } })}
              aria-label={t("backToTicketing")}
            >
              <img src={backButton} alt="" />
            </button>
            <div>
              <p>{t("ticketingEditorTitleEyebrow")}</p>
              <h1>{isEditing ? t("updateSale") : t("createSale")}</h1>
            </div>
          </div>
          <div className="ticketingEditorTopActions">
            {isEditing && (
              <div className="ticketingHeaderActionsMenu">
                <button
                  type="button"
                  className="ticketingHeaderActionsTrigger"
                  aria-expanded={isHeaderActionsOpen}
                  aria-label={t("ticketingSaleActions")}
                  onClick={() => setIsHeaderActionsOpen((current) => !current)}
                >
                  {t("ticketingSaleActions")}
                  <span aria-hidden="true">▾</span>
                </button>
                {isHeaderActionsOpen && (
                  <div className="ticketingHeaderActionsDropdown">
                    <button
                      type="button"
                      className="duplicate"
                      onClick={() => {
                        setIsHeaderActionsOpen(false);
                        setIsDuplicate(true);
                      }}
                    >
                      {t("duplicateSaleShort")}
                    </button>
                    <button
                      type="button"
                      className="delete"
                      onClick={() => {
                        setIsHeaderActionsOpen(false);
                        setIsDelete(true);
                      }}
                    >
                      {t("deleteSaleShort")}
                    </button>
                  </div>
                )}
              </div>
            )}
            <GradientButton onPress={validationForm}>
              {isEditing ? t("updateSale") : t("createSale")}
            </GradientButton>
          </div>
        </div>

        <nav className="ticketingEditorNav" aria-label={t("ticketingSectionNavigation")}>
          {pageSections.map((section) => (
            <button key={section.id} type="button" onClick={() => scrollToSection(section.id)}>
              {section.label}
            </button>
          ))}
        </nav>

        <TicketingSection
          id="ticketing-essential"
          eyebrow={t("ticketingSectionEssential")}
          title={t("ticketingEssentialTitle")}
          description={t("ticketingEssentialDescription")}
          className="essentialSection"
        >
          <div className="ticketingHeroGrid">
            <div className="ticketingHeroMedia">
              <section className="saleAliveContainer">
                <p className="saleAliveTitle">{event.open ? t("saleOpen") : t("saleClose")}</p>
                <GreenRedOultinedButtons
                  greenName={t("open")}
                  redName={t("close")}
                  greenOnPress={() => updateState("open", true)}
                  redOnPress={() => updateState("open", false)}
                  activeGreenCondition={event.open}
                />
              </section>
              <PictureDisplaySelector
                onMediaChange={handleMediaChange}
                initialMediaUrl={event.mediaUrl}
                error={eventErrors.mediaUrl}
                title={event.name}
                isVideo={event.mediaIsVideo}
                onGenerate={handleGenerateCover}
                canGenerate={!!event.name.trim() && !!getCoverDescription()}
                generating={coverGenerating}
                remainingGenerations={coverRemaining}
                generatedCandidates={generatedCovers}
                onSelectGenerated={handleSelectGeneratedCover}
              />
            </div>
            <div className="ticketingHeroFields">
              <section className="nameEndDateContainer">
                <FormField
                  label={t("eventName")}
                  value={event.name}
                  onChange={(value) => updateState("name", value)}
                  type="text"
                  error={eventErrors.name}
                />
                <FormField
                  label={t("salesEndDate")}
                  value={event.endDate || ""}
                  onChange={(value) => updateState("endDate", value)}
                  type="dateTime"
                  error={eventErrors.endDate}
                />
              </section>
              <section className="fromToDates">
                <FormField
                  label={t("salesFromDate")}
                  value={event.eventFromDate || ""}
                  onChange={(value) => updateState("eventFromDate", value)}
                  type="dateTime"
                  error={eventErrors.eventFromDate}
                />
                <FormField
                  label={t("salesToDate")}
                  value={event.eventToDate || ""}
                  onChange={(value) => updateState("eventToDate", value)}
                  type="dateTime"
                  error={eventErrors.eventToDate}
                />
              </section>
            </div>
          </div>
        </TicketingSection>

        <TicketingSection
          id="ticketing-content"
          eyebrow={t("ticketingSectionContent")}
          title={t("ticketingContentTitle")}
          description={t("ticketingContentDescription")}
          className="contentSection"
        >
          <div className="descriptionContainer">
            <article className="descriptionLanguageCard">
            <header className="descriptionLanguageHeader">
              <div className="descriptionLanguageTitle">
                <span>{t("eventDescription")}</span>
              </div>
              <div className="descriptionLanguageActions">
                <div className="descriptionLanguageTabs" role="tablist">
                  {descriptionLanguages.map((lang) => (
                    <button
                      key={lang}
                      type="button"
                      role="tab"
                      aria-selected={activeDescriptionLanguage === lang}
                      className={`descriptionLanguageTab ${
                        activeDescriptionLanguage === lang ? "active" : ""
                      }`}
                      onClick={() => setActiveDescriptionLanguage(lang)}
                    >
                      {lang.toUpperCase()}
                    </button>
                  ))}
                  {!allSelected && (
                    <button
                      type="button"
                      className="descriptionLanguageTab add"
                      onClick={() => setIsAddingNewDescription(true)}
                    >
                      +
                    </button>
                  )}
                </div>
                {descriptionLanguages.length > 1 && (
                  <button
                    type="button"
                    className="descriptionRemoveButton"
                    aria-label={`${t("removeDescription")} (${activeDescriptionLanguage.toUpperCase()})`}
                    title={t("removeDescription")}
                    onClick={() =>
                      setDescriptions((prevDesc) => {
                        const newDescriptions = { ...prevDesc };
                        delete newDescriptions[activeDescriptionLanguage];
                        return newDescriptions;
                      })
                    }
                  >
                    ×
                  </button>
                )}
              </div>
            </header>
            <div className="descriptionFieldContainer">
              <FormField
                label=""
                name={`event-description-${activeDescriptionLanguage}`}
                value={activeDescription}
                onChange={(value) =>
                  setDescriptions((prevDesc) => ({
                    ...prevDesc,
                    [activeDescriptionLanguage]: value,
                  }))
                }
                type="longText"
                error={eventErrors.descriptions}
              />
            </div>
            <div className="descriptionAiActions">
              <OutlinedPinkButton
                className="ai-description-button improve"
                disabled={!!aiTextAction || !event.name.trim() || !activeDescription?.trim()}
                onPressFunction={() => handleRewriteDescription(activeDescriptionLanguage)}
              >
                <span className="ai-description-button-content">
                  {aiTextAction?.type === "rewrite" &&
                    aiTextAction.language === activeDescriptionLanguage && (
                      <span className="ai-description-spinner" aria-hidden="true" />
                    )}
                  <span>
                    {aiTextAction?.type === "rewrite" &&
                    aiTextAction.language === activeDescriptionLanguage
                      ? t("improvingDescriptionWithAi")
                      : t("improveDescriptionWithAi")}
                  </span>
                </span>
              </OutlinedPinkButton>
              <OutlinedPinkButton
                className="ai-description-button translate"
                disabled={!!aiTextAction || !event.name.trim() || !activeDescription?.trim()}
                onPressFunction={() => handleTranslateDescriptions(activeDescriptionLanguage)}
              >
                <span className="ai-description-button-content">
                  {aiTextAction?.type === "translations" &&
                    aiTextAction.language === activeDescriptionLanguage && (
                      <span className="ai-description-spinner" aria-hidden="true" />
                    )}
                  <span>
                    {aiTextAction?.type === "translations" &&
                    aiTextAction.language === activeDescriptionLanguage
                      ? t("generatingAiTranslations")
                      : t("translateAllWithAi")}
                  </span>
                </span>
              </OutlinedPinkButton>
            </div>
          </article>
          </div>
        </TicketingSection>

        <TicketingSection
          id="ticketing-customization"
          eyebrow={t("ticketingSectionCustomization")}
          title={t("ticketingCustomizationTitle")}
          description={t("ticketingCustomizationDescription")}
          className="customizationSection"
        >
          <section className="settingsActionGrid">
            <button
              type="button"
              onClick={() => setShowFormFieldsModal(true)}
              className="settingsActionCard"
            >
              <span>{t("formSettingsEyebrow")}</span>
              <strong>{isEditing ? t("updateForm") : t("createForm")}</strong>
              <small>{t("formSettingsDescription")}</small>
            </button>
            <button
              type="button"
              onClick={() => setShowVisualSettingsModal(true)}
              className="settingsActionCard visual"
            >
              <span>{t("visualSettingsEyebrow")}</span>
              <strong>{t("formVisualCustomization")}</strong>
              <small>{t("visualSettingsDescription")}</small>
            </button>
            <button
              type="button"
              onClick={() => setShowEmailCustomizationModal(true)}
              className="settingsActionCard email"
            >
              <span>{t("ticketEmailCustomizationEyebrow")}</span>
              <strong>{t("ticketEmailCustomizationTitle")}</strong>
              <small>
                {event.ticketEmailCustomization?.enabled
                  ? t("ticketEmailCustomizationEnabled")
                  : t("ticketEmailCustomizationDisabled")}
              </small>
            </button>
          </section>
        </TicketingSection>

        <TicketingSection
          id="ticketing-settings"
          eyebrow={t("ticketingSectionSettings")}
          title={t("ticketingSettingsTitle")}
          description={t("ticketingSettingsDescription")}
          className="settingsSection"
        >
          <div className="ticketingLocationSettingsBlock">
            <TicketingMap
              marker={{
                lat: event.eventLocation?.latitude,
                lng: event.eventLocation?.longitude,
              }}
              address={event.eventAddress}
              setMarker={handleNewCoordinates}
            />

            <div className="ticketingSaleOptionsCard">
            <section className="ticketingOptionRow subdivisionSection">
              <div className="ticketingOptionCopy">
                <h1 className="sectionTitle">{t("pdfTicketsPerOrder")}</h1>
              </div>
              <div className="ticketingSegmentedActions subdivisionButtonsContainer">
                <button
                  type="button"
                  onClick={() => updateState("subdivision", false)}
                  className={`${event.subdivision ? "" : "active"}`}
                >
                  {t("qrPerSale")}
                </button>
                <button
                  type="button"
                  onClick={() => updateState("subdivision", true)}
                  className={`${event.subdivision ? "active" : ""}`}
                >
                  {t("qrPerTicket")}
                </button>
              </div>
            </section>

            <section className="ticketingOptionRow maxEntriesSection">
              <div className="ticketingOptionCopy">
                <h1 className="sectionTitle">{t("maxEntriesTitle")}</h1>
              </div>
              <FormField
                onClick={() => updateState("subdivision", false)}
                className={`${event.subdivision ? "" : "active"}`}
                placeholder={"10, 20, 50, 100, 200, ..."}
                onChange={(value) => {
                  if (/^$|^[1-9][0-9]*$/.test(value)) {
                    updateState("maxEntries", value);
                  }
                }}
                value={event.maxEntries}
                error={eventErrors.maxEntries}
              />
            </section>

            <section className="ticketingOptionRow feeDelegationSection">
              <div className="ticketingOptionCopy">
                <h1 className="sectionTitle">{t("feeDelegationTitle")}</h1>
                <p className="sectionDescription">{t("feeDelegationDescription")}</p>
              </div>
              <div className="ticketingSegmentedActions feeDelegationButtonsContainer">
                <button
                  type="button"
                  onClick={() => updateState("feeDelegated", false)}
                  className={`${event.feeDelegated ? "" : "active"}`}
                >
                  {t("feePaidByCreator")}
                </button>
                <button
                  type="button"
                  onClick={() => updateState("feeDelegated", true)}
                  className={`${event.feeDelegated ? "active" : ""}`}
                >
                  {t("feePaidByBuyer")}
                </button>
              </div>
            </section>
          </div>
          </div>
        </TicketingSection>

        <TicketingSection
          id="ticketing-tickets"
          eyebrow={t("ticketingSectionTickets")}
          title={t("ticketingTicketsTitle")}
          description={t("ticketingTicketsDescription")}
          className="ticketsSection"
        >
          <TicketForm
            handleCreateUpdate={handleCreateUpdateTicket}
            handleReorder={handleReorderTickets}
            tickets={tickets}
            error={eventErrors.tickets}
            handleDelete={handleDeleteTicket}
          />
        </TicketingSection>

        <ValidationButtons />
      </div>

      {showFormFieldsModal && (
        <FormFieldsSettingsModal
          event={event}
          eventErrors={eventErrors}
          handleDeleteField={handleDeleteField}
          handleFieldChange={handleFieldChange}
          handleUpdateCustomFields={handleUpdateCustomFields}
          isEditing={isEditing}
          newCustomFieldError={newCustomFieldError}
          onClose={() => setShowFormFieldsModal(false)}
          t={t}
          updateState={updateState}
          validateNewCustomField={customFieldVerification}
        />
      )}

      {showVisualSettingsModal && (
        <VisualSettingsModal
          defaultPrimaryColor={DEFAULT_PRIMARY_COLOR}
          defaultSecondaryColor={DEFAULT_SECONDARY_COLOR}
          event={event}
          handleLogoChange={handleLogoChange}
          logoFileInputRef={logoFileInputRef}
          onClose={() => setShowVisualSettingsModal(false)}
          setActiveColor={setActiveColor}
          t={t}
          updateState={updateState}
        />
      )}

      <EmailCustomizationModal
        event={event}
        isOpen={showEmailCustomizationModal}
        onClose={() => setShowEmailCustomizationModal(false)}
        onSave={(customization) => {
          updateState("ticketEmailCustomization", customization);
          setShowEmailCustomizationModal(false);
        }}
      />
    </div>
  );
}

export default TicketingCreateEdit;
